import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CRMMergeStatus from './CRMMergeStatus';
import CRMVisitors from '../components/CRMVisitors';
import CRMDonations from '../components/CRMDonations';
import CRMProjects from '../components/CRMProjects';
import UserManagement from '../components/UserManagement';
import ClickUpCRM from '../components/ClickUpCRM';
import ClickUpEmbed from '../components/ClickUpEmbed';
import InvoiceManagement from '../components/InvoiceManagement';
import PhotoCPanel from '../components/PhotoCPanel';
import ProgramReports from '../components/ProgramReports';
import CRMDashboard from '../components/CRMDashboard';
import ContactManagement from '../components/ContactManagement';
import PrayerRequestTracker from '../components/PrayerRequestTracker';
import GamificationLeaderboard from '../components/GamificationLeaderboard';
import SecureCredentials from '../components/SecureCredentials';
import ContentPanel from '../components/ContentPanel';
import FinanceDashboard from '../components/FinanceDashboard';
import ReceiptRepository from '../components/ReceiptRepository';
import NetworkStatus from '../components/NetworkStatus';
import StaffManagement from '../components/StaffManagement';
import ImpactStoriesManagement from '../components/ImpactStoriesManagement';
import ContentSync from '../components/ContentSync';
import FinanceSettings from '../components/FinanceSettings';

const CRM = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeModule, setActiveModule] = useState(null); // null shows dashboard
  const [expandedDepartment, setExpandedDepartment] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const savedAuth = localStorage.getItem('crmAuth');
    if (savedAuth) {
      setIsAuthenticated(true);
      loadStats();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // First try the main admin credentials (hardcoded for backwards compatibility)
      if (credentials.username === 'admin' && credentials.password === 'kioo2025!') {
        const authHeader = btoa('admin:kioo2025!');
        localStorage.setItem('crmAuth', authHeader);
        localStorage.setItem('crmUsername', credentials.username);
        setIsAuthenticated(true);
        loadStats();
        return;
      }
      
      // Try to authenticate using the user management system
      const authHeader = btoa(`${credentials.username}:${credentials.password}`);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/user-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          localStorage.setItem('crmAuth', authHeader);
          localStorage.setItem('crmUsername', credentials.username);
          localStorage.setItem('crmUserRole', data.user?.role || 'staff');
          setIsAuthenticated(true);
          loadStats();
          return;
        }
      }
      
      setError('Invalid username or password');
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const authHeader = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/stats`, {
        headers: { 'Authorization': `Basic ${authHeader}` }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('crmAuth');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setActiveModule(null);
  };

  const departments = [
    {
      id: 'overview',
      name: 'CRM Overview',
      icon: '📊',
      description: 'Dashboard, contacts, and engagement',
      color: 'emerald',
      modules: [
        { id: 'crm-dashboard', name: 'Unified Dashboard', icon: '📊' },
        { id: 'contact-management', name: 'Contact Management', icon: '👥' },
        { id: 'prayer-requests', name: 'Prayer Requests', icon: '🙏' },
        { id: 'gamification', name: 'Leaderboard & Badges', icon: '🏆' }
      ]
    },
    {
      id: 'visitors',
      name: 'Visitor Management',
      icon: '🏠',
      description: 'Track and manage station visitors',
      color: 'blue',
      modules: [
        { id: 'visitors', name: 'View Visitors', icon: '📋' },
        { id: 'add-visitor', name: 'Add New Visitor', icon: '➕' }
      ]
    },
    {
      id: 'finance',
      name: 'Finance & Accounting',
      icon: '💰',
      description: 'Income, expenses, invoices, and financial reports',
      color: 'green',
      modules: [
        { id: 'finance', name: 'Finance Dashboard', icon: '📊' },
        { id: 'receipts', name: 'Receipt Repository', icon: '🧾' },
        { id: 'invoicing', name: 'Invoice Management', icon: '📄' },
        { id: 'donations', name: 'Donations', icon: '💝' },
        { id: 'projects', name: 'Project Funding', icon: '📁' },
        { id: 'finance-settings', name: 'Finance Settings', icon: '⚙️' }
      ]
    },
    {
      id: 'media',
      name: 'Media & Content',
      icon: '📸',
      description: 'Photos, videos, and content management',
      color: 'purple',
      modules: [
        { id: 'photo-gallery', name: 'Photo Gallery', icon: '🖼️' },
        { id: 'staff-management', name: 'Staff Profiles', icon: '👥' },
        { id: 'impact-stories', name: 'Impact Stories', icon: '📖' },
        { id: 'content-sync', name: 'Sync → Production', icon: '🔄' }
      ]
    },
    {
      id: 'reporting',
      name: 'Reports & Analytics',
      icon: '📈',
      description: 'Coordinator reports and monthly analytics',
      color: 'orange',
      modules: [
        { id: 'program-reports', name: 'Program Reports', icon: '📋' },
        { id: 'reporting-dashboard', name: 'Reporting Dashboard', icon: '📈', external: true, link: '/reporting/login' },
        { id: 'reporting-news', name: 'Published News', icon: '📰', external: true, link: '/news' }
      ]
    },
    {
      id: 'admin',
      name: 'System Administration',
      icon: '⚙️',
      description: 'User management, credentials, and settings',
      color: 'gray',
      modules: [
        { id: 'user-management', name: 'User Management', icon: '👤' },
        { id: 'secure-credentials', name: 'Secure Credentials', icon: '🔐' },
        { id: 'merge-status', name: 'Data Merge Status', icon: '🔄' }
      ]
    },
    {
      id: 'website',
      name: 'Website cPanel',
      icon: '🖥️',
      description: 'Manage website content without Emergent',
      color: 'cyan',
      modules: [
        { id: 'cpanel', name: 'Website Control Panel', icon: '🖥️' }
      ]
    },
    {
      id: 'integrations',
      name: 'Integrations',
      icon: '🔗',
      description: 'Third-party integrations',
      color: 'indigo',
      modules: [
        { id: 'clickup-crm', name: 'ClickUp CRM', icon: '📋' },
        { id: 'clickup-embed', name: 'ClickUp Embed', icon: '🔗' }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      green: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      gray: 'from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700',
      indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
      emerald: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
      cyan: 'from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700'
    };
    return colors[color] || colors.blue;
  };

  const handleModuleClick = (module) => {
    if (module.external) {
      navigate(module.link);
    } else {
      setActiveModule(module.id);
    }
  };

  const renderModuleContent = () => {
    switch (activeModule) {
      case 'crm-dashboard':
        return <CRMDashboard />;
      case 'contact-management':
        return <ContactManagement />;
      case 'prayer-requests':
        return <PrayerRequestTracker />;
      case 'gamification':
        return <GamificationLeaderboard />;
      case 'visitors':
        return <CRMVisitors />;
      case 'invoicing':
        return <InvoiceManagement />;
      case 'finance':
        return <FinanceDashboard />;
      case 'finance-settings':
        return <FinanceSettings />;
      case 'receipts':
        return <ReceiptRepository />;
      case 'donations':
        return <CRMDonations />;
      case 'projects':
        return <CRMProjects />;
      case 'photo-gallery':
        return <PhotoCPanel />;
      case 'staff-management':
        return <StaffManagement />;
      case 'impact-stories':
        return <ImpactStoriesManagement />;
      case 'content-sync':
        return <ContentSync />;
      case 'user-management':
        return <UserManagement />;
      case 'secure-credentials':
        return <SecureCredentials />;
      case 'cpanel':
        return <ContentPanel />;
      case 'merge-status':
        return <CRMMergeStatus />;
      case 'clickup-crm':
        return <ClickUpCRM />;
      case 'program-reports':
        return <ProgramReports />;
      case 'clickup-embed':
        return <ClickUpEmbed />;
      default:
        return null;
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Helmet>
          <title>CRM Login - Kioo Radio</title>
        </Helmet>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Kioo Radio CRM</h1>
            <p className="text-gray-600">Central Management System</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all shadow-lg"
            >
              Login to CRM
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main CRM Interface
  if (activeModule) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>CRM - {activeModule} - Kioo Radio</title>
        </Helmet>

        {/* Top Bar */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setActiveModule(null)}
                  className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                >
                  ← Back to Dashboard
                </button>
                <span className="text-gray-300">|</span>
                <h2 className="text-xl font-semibold text-gray-800 capitalize">
                  {activeModule.replace('-', ' ')}
                </h2>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Module Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderModuleContent()}
        </div>
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Helmet>
        <title>CRM Dashboard - Kioo Radio</title>
      </Helmet>

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Kioo Radio CRM</h1>
              <p className="text-gray-600">Central Management System</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Contacts</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_contacts || 0}</p>
                </div>
                <div className="text-4xl">👥</div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Visitors</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_visitors || 0}</p>
                </div>
                <div className="text-4xl">🚶</div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Invoices</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_invoices || 0}</p>
                </div>
                <div className="text-4xl">📄</div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Photos</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_photos || 0}</p>
                </div>
                <div className="text-4xl">📸</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {departments.map((dept) => (
            <div
              key={dept.id}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              {/* Department Header */}
              <div className={`bg-gradient-to-r ${getColorClasses(dept.color)} p-6 text-white`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{dept.icon}</span>
                  <h3 className="text-xl font-bold">{dept.name}</h3>
                </div>
                <p className="text-white/90 text-sm">{dept.description}</p>
              </div>

              {/* Modules List */}
              <div className="p-4 space-y-2">
                {dept.modules.map((module) => (
                  <button
                    key={module.id}
                    onClick={() => handleModuleClick(module)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-left group border border-transparent hover:border-gray-200"
                  >
                    <span className="text-2xl">{module.icon}</span>
                    <span className="flex-1 font-medium text-gray-700 group-hover:text-gray-900">
                      {module.name}
                    </span>
                    <span className="text-gray-400 group-hover:text-gray-600">→</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Network Status Indicator - Offline Support */}
      <NetworkStatus />
    </div>
  );
};

export default CRM;
